'use client';

import { useEffect, useRef, useState } from 'react';
import { HeroCarouselProps } from './types';
import useCarousel from 'hooks/useCarousel';
import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import { calculateOverlayMargin, cn, getColorway } from 'utils/styles';
import CarouselPagination from 'components/shared/Carousel/CarouselPagination';
import HeroConsolidated from 'components/HeroConsolidated/HeroConsolidated';
import { Margins, OverflowVisibleSettings } from 'types/constants';
import AutoHeight from 'embla-carousel-auto-height';
import { HeroVariants } from 'components/HeroConsolidated/constants';
import { HeroImageLocation } from 'components/HeroConsolidated/shared/HeroImage';
import type { CarouselApi } from 'types/sharedComponents';
import { useAutoplayPlugin } from 'hooks/useAutoplayPlugin';
import AutoplayToggleButton from '@/components/shared/Carousel/AutoplayToggleButton';

const HeroCarousel = ({
  sectionName,
  slides,
  color,
  margins,
  mobileMediaLocation,
  scrollCta,
  enrollmentUrl,
}: HeroCarouselProps) => {
  const { current, setApi, scrollTo } = useHeroCarousel();
  // Exclude Full Bleed Hero Variants from Carousel content
  const slidesContent = slides.items.filter(s => s.variant !== HeroVariants.FullBleed);

  const autoPlayOptions = {
    delay: 5000,
    stopOnMouseEnter: true,
    stopOnFocusIn: true,
  };
  const autoplayPlugin = useAutoplayPlugin(autoPlayOptions);

  if (slidesContent.length < 2) {
    return (
      <HeroConsolidated
        {...slidesContent[0]}
        scrollCta={scrollCta}
        mobileMediaLocation={mobileMediaLocation}
        margins={margins}
        color={color}
        enrollmentUrl={enrollmentUrl}
      />
    );
  }

  // Check if mobile media location is bottom and if any of the slides have a notification overlay
  const hasNotificationOverlay =
    mobileMediaLocation === HeroImageLocation.Bottom && slidesContent.some(slide => slide.imageOverlay);

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      data-hero-module
      className={cn(getColorway(color), margins !== Margins.NoBottom ? 'pb-8 sm:pb-16' : 'pb-0', 'overflow-hidden')}>
      <Carousel
        aria-label="Hero carousel"
        aria-roledescription="Hero carousel"
        className={cn('flex flex-col', !hasNotificationOverlay && 'gap-4')}
        setApi={setApi}
        plugins={[AutoHeight(), ...(autoplayPlugin ? [autoplayPlugin] : [])]}
        opts={{
          align: 'center',
          slidesToScroll: 1,
          containScroll: false,
          loop: true,
        }}>
        <CarouselContent
          overflowVisible={
            hasNotificationOverlay ? OverflowVisibleSettings.MobileVertical : OverflowVisibleSettings.None
          }
          className="w-full">
          <div className={'flex *:basis-[100vw] *:shrink-0 *:grow-0 max-sm:*:max-h-min'} aria-live={'polite'}>
            {slidesContent.map((slide, index) => {
              return (
                <HeroConsolidated
                  {...slide}
                  key={index}
                  scrollCta={scrollCta}
                  mobileMediaLocation={mobileMediaLocation}
                  margins={Margins.NoBottom} // Prevent spacing between hero content and pagination
                  color={color}
                  headingLevel={index === 0 ? 'h1' : 'h2'}
                  enrollmentUrl={enrollmentUrl}
                  isSlider={true}
                  slideIndex={index}
                  isInactiveCarouselSlide={index !== current} // Accessibility feature: hides inactive slides from assistive technologies / keyboard users
                />
              );
            })}
          </div>
        </CarouselContent>
        <div
          className={cn(
            'content-wrapper flex justify-end items-center gap-x-6',
            hasNotificationOverlay && calculateOverlayMargin(hasNotificationOverlay),
          )}>
          <CarouselPagination
            total={slidesContent.length}
            current={current}
            onSelect={scrollTo}
            className={'py-2'}
            isTimed
            timeDuration={autoPlayOptions.delay / 1000}
          />
          <AutoplayToggleButton />
        </div>
      </Carousel>
    </section>
  );
};

export default HeroCarousel;

const useHeroCarousel = () => {
  const { current, setApi: setCarouselApi, scrollTo } = useCarousel();
  const apiRef = useRef<CarouselApi>(undefined);
  const [apiReady, setApiReady] = useState(false);

  // Wrap setApi to store reference and trigger effect
  const setApi = (api: CarouselApi) => {
    apiRef.current = api;
    setCarouselApi(api);
    if (api) {
      setApiReady(true);
    }
  };

  // Fix race condition: Stop autoplay if input is already focused during initialization
  useEffect(() => {
    if (!apiReady) return;

    const api = apiRef.current;
    if (!api) return;

    const autoplayPluginInstance = api.plugins()?.autoplay;
    if (!autoplayPluginInstance) return;

    const carouselRoot = api.rootNode();

    const checkAndStopIfInputFocused = () => {
      const activeElement = document.activeElement as HTMLElement;

      // If an input within the carousel already has focus, manually stop autoplay
      if (activeElement?.tagName === 'INPUT' && carouselRoot.contains(activeElement)) {
        autoplayPluginInstance.stop();
      }
    };

    // Check immediately in case autoplay already started before listener attached
    if (autoplayPluginInstance.isPlaying()) {
      checkAndStopIfInputFocused();
    }

    // Also listen for future autoplay:play events (in case it gets restarted)
    const handleAutoplayPlay = () => {
      checkAndStopIfInputFocused();
    };

    api.on('autoplay:play', handleAutoplayPlay);

    // Add explicit focus event listeners to stop autoplay on input focus
    // This works more reliably than the built-in stopOnFocusIn option,
    // especially with fast page loads from Redis cache
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target?.tagName === 'INPUT' && carouselRoot.contains(target)) {
        autoplayPluginInstance.stop();
      }
    };

    carouselRoot.addEventListener('focusin', handleFocusIn, { capture: true });

    return () => {
      api.off('autoplay:play', handleAutoplayPlay);
      carouselRoot.removeEventListener('focusin', handleFocusIn, { capture: true });
    };
  }, [apiReady]);

  // Fix AutoHeight not recalculating when lazy images load
  // Workaround until embla-carousel releases v8.7+ with resize event support
  // See: https://github.com/davidjerleke/embla-carousel/issues/910
  useEffect(() => {
    if (!apiReady) return;

    const api = apiRef.current;
    if (!api) return;

    const autoHeightPlugin = api.plugins()?.autoHeight;
    if (!autoHeightPlugin) return;

    const carouselRoot = api.rootNode();

    // Listen for image load events using event delegation
    const handleImageLoad = (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLImageElement && carouselRoot.contains(target)) {
        // Image loaded, re-measure slide heights after transition
        setTimeout(() => {
          api.reInit();
        }, 500);
      }
    };

    carouselRoot.addEventListener('load', handleImageLoad, { capture: true });

    return () => {
      carouselRoot.removeEventListener('load', handleImageLoad, { capture: true });
    };
  }, [apiReady]);

  return { current, setApi, scrollTo };
};
